<template>
	<section id="modal">
		<div class="read-all--button-wrapper">
			<a
				class="d-flex align-items-center read-all--button"
				@click="handleClickAll"
				>{{ $t("notification_component.mark_read") }}
				<MGIcon icon="chevron-right" />
			</a>
		</div>
		<div class="cards">
			<HeaderNotificationCard
				v-for="(notification, index) in notifications"
				:key="index"
				:notification="notification"
				@close="closeModal()"
			/>
		</div>
	</section>
</template>

<script lang="ts" setup>
import { useNotificationsStore } from "~/stores/UserNotifications";
import { useDebounceFn } from "@vueuse/core";

const notificationsStore = useNotificationsStore();

const notifications = computed(() => notificationsStore.notifications);

const handleClickAll = () => {
	notificationsStore.markAllAsRead();
};

const emit = defineEmits(["toggle-visible"]);

const handleScroll = useDebounceFn((event: Event) => {
	const element = event.target as HTMLElement;
	const bottomReached =
		element.scrollHeight - element.scrollTop <= element.clientHeight + 1;

	if (bottomReached) {
		const totalNotifications = notificationsStore.totalNotifications;
		const currentPage = notificationsStore.currentPage;

		if (notifications.value.length < totalNotifications) {
			notificationsStore.fetchNotifications(Number(currentPage + 1));
		}
	}
}, 300);

const closeModal = () => {
	emit("toggle-visible");
};

const handleClickOutside = (event: MouseEvent) => {
	const modal = document.getElementById("modal");
	// composedPath() is fixed when the event is dispatched; contains(target) is read afterwards. A button that removes itself in its
	// own click handler (the delete dialog's "confirm") was no longer inside the dropdown by then, so confirming a delete closed it.
	if (modal && !event.composedPath().includes(modal)) {
		closeModal();
	}
};

onMounted(() => {
	const modal = document.getElementById("modal");
	if (modal) {
		modal.addEventListener("scroll", handleScroll);
	}
	document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
	const modal = document.getElementById("modal");
	if (modal) {
		modal.removeEventListener("scroll", handleScroll);
	}
	document.removeEventListener("click", handleClickOutside);
});
</script>

<style lang="scss" scoped>
#modal {
	position: absolute;
	width: 400px;
	background-color: var(--card-article-bg);
	z-index: 1000000;
	max-height: 750px;
	top: 50px;
	right: 110px;
	overflow: auto;
	border: 1px solid var(--card-article-border);

	[dir="rtl"] & {
		right: auto;
		left: 0;
	}

	&::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	&::-webkit-scrollbar-button {
		width: 0px;
		height: 0px;
	}

	&::-webkit-scrollbar-thumb {
		background: var(--scroll-thumb);
		border: 15px none transparent;
		border-radius: 0px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: var(--bt-active);
	}

	&::-webkit-scrollbar-thumb:active {
		background: var(--scroll-thumb-active);
	}

	&::-webkit-scrollbar-track {
		background: var(--scroll-track);
		border: 8px none transparent;
		border-radius: 0px;
	}

	&::-webkit-scrollbar-track:hover {
		background: var(--scroll-track);
	}

	&::-webkit-scrollbar-track:active {
		background: var(--scroll-track-active);
	}

	&::-webkit-scrollbar-corner {
		background: transparent;
	}
}

.cards {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	align-self: stretch;
}

.read-all--button-wrapper {
	padding: 24px;
	font-size: 14px;
	font-weight: 400;
	line-height: 20px;
    border-bottom: 1px solid var(--body-bg);
	.read-all--button {
		transition: all 0.1s ease-in;
		color: var(--sidebar-menus-active-bg);

		&:hover {
			cursor: pointer;
		}
	}
}

i {
	font-size: 12px;
}

@media screen and (max-width: 576px) {
	#modal {
		position: absolute;
		min-width: 300px;
		width: 100%;
		width: 100%;
		top: 60px;
		left: 20px;
		z-index: 999;
		max-height: 500px;
	}
}
</style>
